import { cn } from '../cn.js';
import { ChartTooltip } from './internal/ChartTooltip.js';
import { useChartHover } from './internal/useChartHover.js';
import { useDrawIn } from './internal/useDrawIn.js';

export interface BarChartDatum {
  id: string;
  label: string;
  value: number;
  /** Drawn as a dashed tick at this value — an expected competency level, a target check-in
   *  confidence. `null`/omitted draws nothing, so a mixed dataset can reference some rows only. */
  reference?: number | null;
  /** Extra line in the hover tooltip — "3 of 4 raters", a date, anything beyond the value itself. */
  meta?: string;
}

export interface BarChartProps {
  data: BarChartDatum[];
  /** [min, max] for the value axis. Defaults to [0, max(value, reference)] — unlike `LineChart`,
   *  which deliberately zooms to the data's own range, a bar's length IS its meaning, so the
   *  scale always starts at 0 and a caller only overrides the ceiling (e.g. a fixed 1-5 rating
   *  scale that should read the same width across every review, not rescale per dataset). */
  domain?: [number, number];
  /** A token reference, e.g. "var(--accent)". Never a status token — `--success`/`--warning`/
   *  `--danger` stay reserved for actual status elsewhere (a goal's state, a stepper's step),
   *  not for a plain categorical bar that carries no status of its own. */
  color?: string;
  /** Caption for the dashed reference tick, rendered once beneath the chart — not per row — since
   *  every `reference` on one chart means the same thing ("Expected level", "Team average"). */
  referenceLabel?: string;
  valueFormat?: (value: number) => string;
  /** Renders each bar's formatted value end-aligned on the label's own line — for a short,
   *  headline list (a handful of competency scores) where the reading belongs on the row itself,
   *  not just in the hover tooltip. Off by default so charts built for comparison/scanning across
   *  many rows (a hover + a separate legend already carries the number there) don't gain a label
   *  they weren't designed around. */
  showValues?: boolean;
  height?: number;
  className?: string;
  ariaLabel?: string;
}

const PADDING = { top: 8, right: 16, bottom: 24, left: 16 };
const VIEW_WIDTH = 600;
const ROW_HEIGHT = 34;
const ROW_GAP = 10;
const BAR_THICKNESS = 14;
// Gap between the label/value text baseline and the bar below it — a fixed offset from the row's
// top, not a thickness-dependent centering trick, so the rhythm stays consistent as BAR_THICKNESS
// changes.
const BAR_OFFSET_Y = 16;
// The value bar's draw-in sweep — long enough to read as a deliberate reveal on a chart of real
// review/goal data (not a flicker), short enough not to make the page feel like it's loading.
const DRAW_IN_DURATION = '560ms';
// Per-row stagger so a multi-row chart reads as one sweep across the bars rather than every bar
// snapping into place at once.
const DRAW_IN_STAGGER = 40;

/**
 * Single-series magnitude chart — a competency's rating against its expected level, a goal's
 * progress against its target. `Ring`/`ProgressBar` carry a fixed 0-100 percent; this is for a
 * named list of independent quantities on their own scale (a 1-5 rating, a raw count), which is
 * exactly what neither of those components models. Hand-rolled SVG, same reasoning as
 * `LineChart.tsx`: no charting dependency exists in this repo for a handful of bars per screen.
 *
 * `viewBox`-scaled to a fixed internal coordinate space and rendered at 100% width, so it reflows
 * with its container instead of needing a measured pixel width from the caller.
 */
export function BarChart({
  data,
  domain,
  color = 'var(--accent)',
  referenceLabel,
  valueFormat = (value) => String(value),
  showValues = false,
  height,
  className,
  ariaLabel = 'Bar chart',
}: BarChartProps) {
  const hover = useChartHover(data.length);
  const [drawInRef, mounted] = useDrawIn<HTMLDivElement>();

  const allValues = data.flatMap((d) => (d.reference != null ? [d.value, d.reference] : [d.value]));
  const domainMin = domain ? domain[0] : 0;
  const domainMax = domain ? domain[1] : Math.max(1, ...allValues, 0);
  const range = domainMax - domainMin || 1;
  const showReferenceCaption = Boolean(referenceLabel) && data.some((d) => d.reference != null);
  const captionHeight = showReferenceCaption ? 18 : 0;

  const active = hover.activeIndex != null ? data[hover.activeIndex] : undefined;

  if (data.length === 0) return null;

  const plotLeft = PADDING.left;
  const plotRight = VIEW_WIDTH - PADDING.right;
  const plotWidth = plotRight - plotLeft;
  const plotHeight = data.length * (ROW_HEIGHT + ROW_GAP) - ROW_GAP;
  const viewHeight = height ?? PADDING.top + plotHeight + PADDING.bottom + captionHeight;
  const xFor = (value: number) => plotLeft + (plotWidth * (value - domainMin)) / range;

  return (
    <div ref={drawInRef} className={cn('relative w-full', className)}>
      <svg
        viewBox={`0 0 ${VIEW_WIDTH} ${viewHeight}`}
        width="100%"
        height={viewHeight}
        role="img"
        aria-label={ariaLabel}
        preserveAspectRatio="none"
        {...hover.rootProps}
      >
        {data.map((d, i) => {
          const rowTop = PADDING.top + i * (ROW_HEIGHT + ROW_GAP);
          const barY = rowTop + BAR_OFFSET_Y;
          const barWidth = Math.max(0, xFor(d.value) - plotLeft);
          const isActive = hover.activeIndex === i;
          return (
            <g key={d.id}>
              <text x={plotLeft} y={rowTop + 9} fontSize={10} fill="var(--fg-subtle)">
                {d.label}
              </text>
              {showValues && (
                <text
                  x={plotRight}
                  y={rowTop + 9}
                  textAnchor="end"
                  fontSize={11}
                  fontWeight={600}
                  fill="var(--fg)"
                  className="tabular-nums"
                >
                  {valueFormat(d.value)}
                </text>
              )}
              <rect
                x={plotLeft}
                y={barY}
                width={plotWidth}
                height={BAR_THICKNESS}
                rx={4}
                fill="var(--bg-subtle)"
              />
              <rect
                x={plotLeft}
                y={barY}
                width={barWidth}
                height={BAR_THICKNESS}
                rx={4}
                fill={color}
                opacity={hover.activeIndex == null || isActive ? 1 : 0.6}
                // Draw-in: grows from the track's left edge, not the SVG viewport's — `fill-box`
                // anchors the transform to this rect's own geometry, so `scaleX(0)` collapses it
                // to nothing exactly where the real bar starts rather than at x=0.
                style={{
                  transformBox: 'fill-box',
                  transformOrigin: 'left',
                  transform: mounted ? 'scaleX(1)' : 'scaleX(0)',
                  transition: `transform ${DRAW_IN_DURATION} var(--ease)`,
                  transitionDelay: `${i * DRAW_IN_STAGGER}ms`,
                }}
              />
              {d.reference != null && (
                <line
                  x1={xFor(d.reference)}
                  y1={barY - 3}
                  x2={xFor(d.reference)}
                  y2={barY + BAR_THICKNESS + 3}
                  stroke="var(--fg-subtle)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
              )}
              {isActive && (
                <rect
                  x={plotLeft - 2}
                  y={barY - 2}
                  width={plotWidth + 4}
                  height={BAR_THICKNESS + 4}
                  rx={4}
                  fill="none"
                  stroke="var(--border-strong)"
                  strokeWidth={1}
                />
              )}
              {/* Transparent hit band across the full row (label included) — the row is often
                  the easiest target to aim for, not just the thin bar itself. */}
              <rect
                x={0}
                y={rowTop}
                width={VIEW_WIDTH}
                height={ROW_HEIGHT + ROW_GAP}
                fill="transparent"
                onPointerEnter={() => hover.setActiveIndex(i)}
                onPointerMove={() => hover.setActiveIndex(i)}
              />
            </g>
          );
        })}
        {showReferenceCaption && (
          <text
            x={plotRight}
            y={viewHeight - 6}
            textAnchor="end"
            fontSize={10}
            fill="var(--fg-subtle)"
          >
            {referenceLabel}
          </text>
        )}
      </svg>
      {active && hover.activeIndex != null && (
        <ChartTooltip
          x={(xFor(active.value) / VIEW_WIDTH) * 100}
          y={
            ((PADDING.top + hover.activeIndex * (ROW_HEIGHT + ROW_GAP) + ROW_HEIGHT / 2) /
              viewHeight) *
            100
          }
        >
          <div className="font-semibold text-fg">{active.label}</div>
          <div>{valueFormat(active.value)}</div>
          {active.meta && <div className="text-fg-subtle">{active.meta}</div>}
        </ChartTooltip>
      )}
    </div>
  );
}
